import { FormEvent, useMemo, useState } from 'react'
import {
  Episode,
  FeedingStatus,
  FluidType,
  appendTreatmentEvent,
  calculateDocumentedFluidExposure,
  calculateIvInsulinWindow,
  latestTreatmentEventWith,
  makeTreatmentEvent
} from './clinical/episode'
import {
  calculatePlannedOverlapHours,
  calculateTransitionEstimate,
  evaluateTherapyGuidance,
  transitionPlanIsComplete
} from './clinical/therapy'
import { RuleReferences } from './ReferenceLinks'
import { saveEpisode } from './storage'

const num = (value: FormDataEntryValue | null): number | undefined => {
  if (typeof value !== 'string' || value.trim() === '') return undefined
  const parsed = Number(value.replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : undefined
}

const toIso = (value: FormDataEntryValue | null): string | undefined => {
  if (typeof value !== 'string' || !value) return undefined
  const date = new Date(value)
  return Number.isFinite(date.getTime()) ? date.toISOString() : undefined
}

const toLocalInput = (iso = new Date().toISOString()) => {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const timeLabel = (iso: string) => new Intl.DateTimeFormat('it-IT', {
  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
}).format(new Date(iso))

const rangeLabel = (low: number, high: number) => low === high ? `${low} U/die` : `${low}–${high} U/die`
const fluidTypeLabel: Record<FluidType, string> = {
  balanced_crystalloid: 'Cristalloide bilanciato',
  saline_09: 'NaCl 0,9%',
  saline_045: 'NaCl 0,45%',
  dextrose_5: 'Glucosio 5%',
  dextrose_10: 'Glucosio 10%',
  other: 'Altro fluido'
}

const fluidRateReference = (low?: number, high?: number) => low !== undefined && high !== undefined ? `${low}–${high} mL/h` : 'Individualizzare'

export default function TherapyPanel({ episode, onChange }: { episode: Episode; onChange: (episode: Episode) => void }) {
  const guidance = useMemo(() => evaluateTherapyGuidance(episode), [episode])
  const baseline = episode.observations[0].input
  const latest = episode.observations[episode.observations.length - 1]
  const automaticRisk = Boolean(baseline.frailty || baseline.advancedKidneyDisease || baseline.dialysis)
  const existingPlan = episode.transitionPlan
  const [risk, setRisk] = useState(existingPlan?.hypoglycemiaRisk ?? automaticRisk)
  const [homeTdd, setHomeTdd] = useState<number | undefined>(existingPlan?.homeTddUnits)
  const [ivStableAccepted, setIvStableAccepted] = useState(Boolean(existingPlan?.ivWindowAcceptedAsStable))
  const [transitionError, setTransitionError] = useState('')
  const [eventSaved, setEventSaved] = useState(false)
  const [eventError, setEventError] = useState('')
  const [planSaved, setPlanSaved] = useState(false)

  const ivWindow = useMemo(() => calculateIvInsulinWindow(episode, new Date().toISOString(), 6), [episode])
  const fluidExposure = useMemo(() => calculateDocumentedFluidExposure(episode), [episode])
  const insulinEvents = useMemo(() => [...(episode.treatmentEvents ?? [])]
    .filter((item) => item.insulinUnitsHour !== undefined)
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime()), [episode.treatmentEvents])
  const currentInsulin = insulinEvents[0]?.insulinUnitsHour ?? latest.treatment?.insulinUnitsHour
  const currentFluidRate = latestTreatmentEventWith(episode, 'fluidMlHour')?.fluidMlHour ?? latest.treatment?.fluidMlHour
  const currentFluidType = latestTreatmentEventWith(episode, 'fluidType')?.fluidType
  const currentKPerL = latestTreatmentEventWith(episode, 'potassiumMmolPerL')?.potassiumMmolPerL
  const currentKRate = latestTreatmentEventWith(episode, 'potassiumMmolHour')?.potassiumMmolHour
  const transition = useMemo(() => calculateTransitionEstimate({
    weightKg: baseline.weightKg,
    hypoglycemiaRisk: risk,
    homeTddUnits: homeTdd,
    stableIvRateUnitsHour: ivStableAccepted && ivWindow.complete ? ivWindow.averageUnitsHour : undefined
  }), [baseline.weightKg, risk, homeTdd, ivStableAccepted, ivWindow])

  const persist = async (next: Episode) => {
    onChange(next)
    await saveEpisode(next)
  }

  const addTreatmentEvent = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = event.currentTarget
    const fd = new FormData(form)
    const occurredAt = toIso(fd.get('occurredAt'))
    const insulinUnitsHour = num(fd.get('insulinUnitsHour'))
    const fluidMlHour = num(fd.get('fluidMlHour'))
    const potassiumMmolPerL = num(fd.get('potassiumMmolPerL'))
    const potassiumMmolHour = num(fd.get('potassiumMmolHour'))
    const fluidTypeValue = String(fd.get('fluidType') || '')
    const fluidType = fluidTypeValue ? fluidTypeValue as FluidType : undefined
    const note = String(fd.get('note') || '').trim() || undefined

    const invalid = [insulinUnitsHour, fluidMlHour, potassiumMmolPerL, potassiumMmolHour].some((value) => value !== undefined && value < 0)
    const hasData = insulinUnitsHour !== undefined || fluidMlHour !== undefined || fluidType !== undefined || potassiumMmolPerL !== undefined || potassiumMmolHour !== undefined
    if (!occurredAt || invalid || !hasData) {
      setEventError('Inserire data/ora e almeno una modifica terapeutica valida.')
      return
    }

    setEventError('')
    const next = appendTreatmentEvent(episode, makeTreatmentEvent(occurredAt, {
      insulinUnitsHour,
      fluidMlHour,
      fluidType,
      potassiumMmolPerL,
      potassiumMmolHour,
      note
    }))
    await persist(next)
    form.reset()
    setEventSaved(true)
    window.setTimeout(() => setEventSaved(false), 1600)
  }

  const recalculate = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const fd = new FormData(event.currentTarget)
    setRisk(fd.get('hypoglycemiaRisk') === 'on')
    setHomeTdd(num(fd.get('homeTddUnits')))
    setIvStableAccepted(fd.get('ivWindowStable') === 'on')
  }

  const documentTransition = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const fd = new FormData(event.currentTarget)
    const plan = {
      status: 'documented' as const,
      selectedTddUnits: num(fd.get('selectedTddUnits')),
      basalUnits: num(fd.get('basalUnits')),
      basalGivenAt: toIso(fd.get('basalGivenAt')),
      ivStopPlannedAt: toIso(fd.get('ivStopPlannedAt')),
      feedingStatus: (String(fd.get('feedingStatus') || 'unknown') as FeedingStatus),
      prandialPlan: String(fd.get('prandialPlan') || '').trim(),
      correctionPlan: String(fd.get('correctionPlan') || '').trim(),
      hypoglycemiaRisk: risk,
      homeTddUnits: homeTdd,
      ivWindowAcceptedAsStable: ivStableAccepted,
      documentedAt: new Date().toISOString()
    }
    const overlap = calculatePlannedOverlapHours(plan)
    if (!transitionPlanIsComplete(plan)) {
      setTransitionError(overlap !== undefined && overlap < 2
        ? `Overlap pianificato ${overlap} h: per il percorso standard servono almeno 2 h.`
        : 'Completa TDD scelto, basale, orari, stato alimentare e piani prandiale/correttivo.')
      return
    }
    setTransitionError('')
    await persist({ ...episode, transitionPlan: plan })
    setPlanSaved(true)
    window.setTimeout(() => setPlanSaved(false), 1800)
  }

  const documentAlternative = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const fd = new FormData(event.currentTarget)
    const reason = String(fd.get('alternativeReason') || '').trim()
    if (!reason) {
      setTransitionError('Per il percorso alternativo è necessaria una motivazione.')
      return
    }
    setTransitionError('')
    await persist({
      ...episode,
      transitionPlan: {
        status: 'alternative',
        alternativeReason: reason,
        documentedAt: new Date().toISOString()
      }
    })
    setPlanSaved(true)
    window.setTimeout(() => setPlanSaved(false), 1800)
  }

  const recentEvents = useMemo(() => [...(episode.treatmentEvents ?? [])]
    .sort((a, b) => new Date(b.occurredAt).getTime() - new Date(a.occurredAt).getTime())
    .slice(0, 8), [episode.treatmentEvents])
  const resolved = guidance.phase === 'resolved'
  const resolutionPending = guidance.phase === 'resolution_pending'
  const unconfirmedPattern = Boolean(latest.result.provisionalPattern)
  const potassiumReference = guidance.potassium.replacementRateMmolHour !== undefined
    ? `${guidance.potassium.replacementRateMmolHour} mmol/h`
    : guidance.potassium.fluidSupplementMmolL
      ? `${guidance.potassium.fluidSupplementMmolL.low}–${guidance.potassium.fluidSupplementMmolL.high} mmol/L`
      : guidance.potassium.status === 'hold_supplement'
        ? 'No K iniziale'
        : '—'
  const fluidReference = guidance.fluid.referenceRateMlHour
    ? fluidRateReference(guidance.fluid.referenceRateMlHour.low, guidance.fluid.referenceRateMlHour.high)
    : guidance.fluid.fluidReference ?? 'Individualizzare'

  return (
    <section className="therapy-panel" id="terapia-guidata">
      <div className="therapy-heading">
        <div>
          <p className="section-label">Terapia guidata · v0.5</p>
          <h3>{guidance.title}</h3>
        </div>
        <span className={`therapy-phase ${guidance.phase}`}>{guidance.phase.replaceAll('_', ' ')}</span>
      </div>

      <div className="therapy-guidance-grid therapy-guidance-grid-v4">
        <div className={`therapy-card ${guidance.potassiumStatus}`}>
          <span>Potassio</span>
          <strong>{latest.input.potassiumMmolL ?? '—'} mmol/L</strong>
          <small>{potassiumReference}</small>
        </div>
        <div className={`therapy-card fluid-${guidance.fluid.status}`}>
          <span>Fluidi · riferimento</span>
          <strong>{fluidReference}</strong>
          <small>{guidance.fluid.fluidReference ?? 'Secondo stato clinico'}</small>
        </div>
        <div className="therapy-card">
          <span>Fluidi registrati</span>
          <strong>{currentFluidRate !== undefined ? `${currentFluidRate} mL/h` : '—'}</strong>
          <small>{currentFluidType ? fluidTypeLabel[currentFluidType] : `${fluidExposure.volumeMl} mL documentati`}</small>
        </div>
        <div className={`therapy-card ${resolutionPending ? 'attention' : ''}`}>
          <span>Insulina EV · riferimento</span>
          <strong>{resolved ? 'Non applicabile' : resolutionPending ? 'Non aumentare' : guidance.insulinUnitsHour !== undefined ? `${guidance.insulinUnitsHour} U/h` : unconfirmedPattern ? 'Non automatizzata' : '—'}</strong>
          <small>{resolved ? 'Crisi metabolica risolta' : resolutionPending ? 'Confermare risoluzione prima di cambiare fase' : guidance.insulinUnitsKgHour !== undefined ? `${guidance.insulinUnitsKgHour} U/kg/h` : unconfirmedPattern ? 'Diagnosi non confermata senza chetoni' : 'Non calcolabile'}</small>
        </div>
        <div className="therapy-card">
          <span>Insulina registrata</span>
          <strong>{currentInsulin !== undefined ? `${currentInsulin} U/h` : '—'}</strong>
          <small>Ultimo evento insulinico documentato</small>
        </div>
        <div className={`therapy-card ${guidance.dextrosePhase ? 'attention' : ''}`}>
          <span>Glucosio EV</span>
          <strong>{resolved ? 'Non richiesto' : resolutionPending ? 'Da rivalutare' : guidance.dextrosePhase ? 'Da associare' : unconfirmedPattern ? 'Non automatizzato' : 'Non indicato'}</strong>
          <small>{guidance.dextrosePhase ? 'DKA non risolta con glicemia <250' : resolutionPending ? 'Risoluzione da confermare' : 'Valutare il contesto clinico'}</small>
        </div>
      </div>

      <div className="therapy-explanation">{guidance.message}</div>
      <RuleReferences ids={['insulin_iv']} />

      <div className="electrolyte-fluid-guidance">
        <article className={`guidance-detail fluid-${guidance.fluid.status}`}>
          <span>Fluidoterapia</span>
          <strong>{guidance.fluid.title}</strong>
          <p>{guidance.fluid.message}</p>
          {guidance.fluid.referenceRateMlHour && <small>Range di riferimento: {guidance.fluid.referenceRateMlHour.low}–{guidance.fluid.referenceRateMlHour.high} mL/h.</small>}
          <RuleReferences ids={['fluids']} />
        </article>
        <article className={`guidance-detail potassium-${guidance.potassium.status}`}>
          <span>Potassio</span>
          <strong>{guidance.potassium.title}</strong>
          <p>{guidance.potassium.message}</p>
          <small>Monitorare K 2 h dopo l’avvio dell’insulina e poi ogni 4 h durante la crisi, salvo necessità di controlli più ravvicinati.</small>
          <RuleReferences ids={['potassium']} />
        </article>
      </div>

      <div className="fluid-documentation-summary">
        <div><span>Volume EV documentato</span><strong>{fluidExposure.volumeMl} mL</strong><small>{fluidExposure.coveredHours}/{fluidExposure.intervalHours} h coperte</small></div>
        <div><span>Copertura registrazione fluidi</span><strong>{fluidExposure.completeFromEpisodeStart ? 'Completa' : 'Parziale'}</strong><small>dal T0 all’ora corrente</small></div>
        <div><span>K nel fluido registrato</span><strong>{currentKPerL !== undefined ? `${currentKPerL} mmol/L` : '—'}</strong><small>{currentKRate !== undefined ? `${currentKRate} mmol/h separati` : 'nessuna velocità separata'}</small></div>
      </div>

      <details className="treatment-events" open>
        <summary>Eventi terapeutici EV</summary>
        <form onSubmit={addTreatmentEvent} className="treatment-event-form treatment-event-form-v4">
          <label><span>Data/ora modifica</span><input type="datetime-local" name="occurredAt" defaultValue={toLocalInput()} required /></label>
          <label><span>Insulina</span><div className="input-wrap"><input type="number" min="0" step="any" name="insulinUnitsHour" inputMode="decimal" /><small>U/h</small></div></label>
          <label><span>Fluido</span><select name="fluidType" defaultValue=""><option value="">Nessuna modifica</option><option value="balanced_crystalloid">Cristalloide bilanciato</option><option value="saline_09">NaCl 0,9%</option><option value="saline_045">NaCl 0,45%</option><option value="dextrose_5">Glucosio 5%</option><option value="dextrose_10">Glucosio 10%</option><option value="other">Altro</option></select></label>
          <label><span>Velocità fluidi</span><div className="input-wrap"><input type="number" min="0" step="any" name="fluidMlHour" inputMode="decimal" /><small>mL/h</small></div></label>
          <label><span>K nel fluido</span><div className="input-wrap"><input type="number" min="0" step="any" name="potassiumMmolPerL" inputMode="decimal" /><small>mmol/L</small></div></label>
          <label><span>K separato</span><div className="input-wrap"><input type="number" min="0" step="any" name="potassiumMmolHour" inputMode="decimal" /><small>mmol/h</small></div></label>
          <label className="event-note"><span>Nota</span><input type="text" name="note" placeholder="es. cambio fluido / K aggiunto / riduzione insulina" /></label>
          <button type="submit" className="secondary">Registra modifica</button>
        </form>
        <p className="event-helper">Registrare solo ciò che cambia. Per l’insulina, documentare anche lo stop come 0 U/h. Le concentrazioni e velocità di K restano dati documentati e non sostituiscono i controlli di sicurezza locali.</p>
        {eventError && <p className="treatment-event-error">{eventError}</p>}
        {eventSaved && <p className="saved">Evento terapeutico registrato.</p>}
        {!!recentEvents.length && <div className="treatment-event-list">{recentEvents.map((item) => {
          const parts = [
            item.insulinUnitsHour !== undefined ? `Ins ${item.insulinUnitsHour} U/h` : '',
            item.fluidType ? fluidTypeLabel[item.fluidType] : '',
            item.fluidMlHour !== undefined ? `${item.fluidMlHour} mL/h` : '',
            item.potassiumMmolPerL !== undefined ? `K ${item.potassiumMmolPerL} mmol/L` : '',
            item.potassiumMmolHour !== undefined ? `K ${item.potassiumMmolHour} mmol/h` : ''
          ].filter(Boolean)
          return <div key={item.id}><strong>{parts.join(' · ') || 'Evento'}</strong><span>{timeLabel(item.occurredAt)}</span>{item.note && <small>{item.note}</small>}</div>
        })}</div>}
      </details>

      {guidance.transitionState === 'candidate' && (
        <div className="transition-note"><strong>Transizione non ancora attiva.</strong> Prima confermare clinicamente la risoluzione dell’episodio.</div>
      )}

      {guidance.transitionState === 'ready' && (
        <details className="transition-panel" id="transizione-ev-sc" open>
          <summary>Transizione EV → SC</summary>
          <RuleReferences ids={['transition']} />

          <div className={`iv-window ${ivWindow.complete ? 'complete' : 'incomplete'}`}>
            <div><span>Finestra EV documentata</span><strong>{ivWindow.coveredHours}/{ivWindow.requestedHours} h</strong></div>
            <div><span>Media temporale</span><strong>{ivWindow.averageUnitsHour !== undefined ? `${ivWindow.averageUnitsHour} U/h` : '—'}</strong></div>
            <div><span>Proiezione grezza</span><strong>{ivWindow.projectedTddUnits !== undefined ? `${ivWindow.projectedTddUnits} U/die` : '—'}</strong></div>
            <div><span>Modifiche insuliniche</span><strong>{ivWindow.changesInWindow}</strong></div>
          </div>
          {!ivWindow.complete && <p className="iv-window-note">La finestra di 6 h non è completamente documentata: la proiezione EV non viene proposta come stima utilizzabile.</p>}

          <form onSubmit={recalculate} className="estimate-form">
            <div className="transition-inputs">
              <label><span>Peso</span><div className="input-wrap"><input name="weightKg" type="number" step="any" inputMode="decimal" defaultValue={baseline.weightKg ?? ''} readOnly /><small>kg</small></div></label>
              <label><span>TDD domiciliare</span><div className="input-wrap"><input name="homeTddUnits" type="number" step="any" inputMode="decimal" defaultValue={homeTdd ?? ''} /><small>U/die</small></div></label>
              <label className="risk-check"><input name="hypoglycemiaRisk" type="checkbox" defaultChecked={risk} /> <span>Rischio ipoglicemico / fragilità / insufficienza renale</span></label>
              <label className={`risk-check ${!ivWindow.complete ? 'disabled-check' : ''}`}><input name="ivWindowStable" type="checkbox" disabled={!ivWindow.complete} defaultChecked={ivStableAccepted} /> <span>Intervallo EV ritenuto clinicamente stabile e utilizzabile</span></label>
            </div>
            <button className="secondary transition-calc" type="submit">Ricalcola stime</button>
          </form>

          <div className="transition-estimates">
            <div><span>Stima ponderale</span><strong>{transition.weightBased ? rangeLabel(transition.weightBased.low, transition.weightBased.high) : '—'}</strong><small>{transition.weightBased?.basis ?? 'Peso non disponibile'}</small></div>
            <div><span>Dose domiciliare</span><strong>{transition.homeTddUnits !== undefined ? `${transition.homeTddUnits} U/die` : '—'}</strong><small>Metodo alternativo, da contestualizzare</small></div>
            <div><span>Proiezione da EV</span><strong>{transition.ivDerivedTddUnits !== undefined ? `${transition.ivDerivedTddUnits} U/die` : '—'}</strong><small>{transition.ivDerivedTddUnits !== undefined ? 'Media documentata accettata × 24 h' : 'Richiede 6 h documentate + conferma di stabilità'}</small></div>
            <div className="overlap-card"><span>Overlap operativo</span><strong>{transition.overlapHours} h</strong><small>Basale SC prima di sospendere EV</small></div>
          </div>

          <form onSubmit={documentTransition} className="transition-checklist">
            <h4>Documentazione della transizione</h4>
            <div className="transition-check-grid">
              <label><span>TDD scelto</span><div className="input-wrap"><input name="selectedTddUnits" type="number" min="0" step="any" defaultValue={existingPlan?.selectedTddUnits ?? ''} /><small>U/die</small></div></label>
              <label><span>Quota basale scelta</span><div className="input-wrap"><input name="basalUnits" type="number" min="0" step="any" defaultValue={existingPlan?.basalUnits ?? ''} /><small>U</small></div></label>
              <label><span>Ora basale SC</span><input name="basalGivenAt" type="datetime-local" defaultValue={existingPlan?.basalGivenAt ? toLocalInput(existingPlan.basalGivenAt) : ''} /></label>
              <label><span>Stop EV previsto</span><input name="ivStopPlannedAt" type="datetime-local" defaultValue={existingPlan?.ivStopPlannedAt ? toLocalInput(existingPlan.ivStopPlannedAt) : ''} /></label>
              <label><span>Stato alimentare</span><select name="feedingStatus" defaultValue={existingPlan?.feedingStatus ?? 'unknown'}><option value="unknown">Non specificato</option><option value="fasting">Digiuno</option><option value="oral">Alimentazione orale</option><option value="enteral">Nutrizione enterale</option><option value="parenteral">Nutrizione parenterale</option></select></label>
              <label className="wide-field"><span>Regime prandiale</span><input name="prandialPlan" type="text" defaultValue={existingPlan?.prandialPlan ?? ''} placeholder="es. non previsto per digiuno / schema secondo protocollo" /></label>
              <label className="wide-field"><span>Regime correttivo</span><input name="correctionPlan" type="text" defaultValue={existingPlan?.correctionPlan ?? ''} placeholder="schema o riferimento al protocollo locale" /></label>
            </div>
            {transitionError && <div className="transition-error">{transitionError}</div>}
            <button className="primary document-transition" type="submit">Documenta transizione</button>
          </form>

          <form onSubmit={documentAlternative} className="alternative-path">
            <label><span>Percorso alternativo / nessuna transizione EV→SC</span><input name="alternativeReason" type="text" defaultValue={existingPlan?.alternativeReason ?? ''} placeholder="Motivazione obbligatoria" /></label>
            <button type="submit" className="secondary">Registra percorso alternativo</button>
          </form>

          {planSaved && <p className="saved">Percorso di transizione aggiornato.</p>}
          {existingPlan?.status === 'documented' && transitionPlanIsComplete(existingPlan) && <div className="transition-documented">Transizione documentata. L’episodio può essere chiuso.</div>}
          {existingPlan?.status === 'alternative' && existingPlan.alternativeReason && <div className="transition-documented">Percorso alternativo documentato: {existingPlan.alternativeReason}</div>}

          <p className="transition-risk-state">Rischio ipoglicemico: <strong>{risk ? 'sì' : 'no'}</strong></p>
          <ul className="transition-notes">{transition.notes.map((note) => <li key={note}>{note}</li>)}</ul>
        </details>
      )}
    </section>
  )
}
